import React from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FileText, Video, Presentation, FolderOpen } from 'lucide-react'
import type { ClientAssetsForm } from '@/types/client'

interface ClientAssetsStepProps {
  data: ClientAssetsForm
  onChange: (data: Partial<ClientAssetsForm>) => void
  isLoading?: boolean
}

export function ClientAssetsStep({ data, onChange, isLoading }: ClientAssetsStepProps) {
  const handleInputChange = (field: keyof ClientAssetsForm, value: string) => {
    onChange({ [field]: value })
  }

  return (
    <div className="space-y-6">
      {/* Case Studies */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <CardTitle>Case Studies</CardTitle>
          </div>
          <CardDescription>Link to client case studies and success stories</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="case_studies_url">Case Studies Page URL</Label>
            <Input
              id="case_studies_url"
              type="url"
              value={data.case_studies_url || ''}
              onChange={(e) => handleInputChange('case_studies_url', e.target.value)}
              placeholder="https://example.com/case-studies"
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Public or shared link to case studies relevant to this client
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Demo Recordings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Video className="h-5 w-5 text-primary" />
            <CardTitle>Demo Recordings</CardTitle>
          </div>
          <CardDescription>Folder with product demo videos and recordings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="demo_recordings_url">Demo Recordings Folder URL</Label>
            <Input
              id="demo_recordings_url"
              type="url"
              value={data.demo_recordings_url || ''}
              onChange={(e) => handleInputChange('demo_recordings_url', e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Google Drive, Dropbox, or other cloud storage link to demo recordings
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Sales Collateral */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Presentation className="h-5 w-5 text-primary" />
            <CardTitle>Decks & Sales Collateral</CardTitle>
          </div>
          <CardDescription>Presentation decks, one-pagers, and sales materials</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="sales_collateral_url">Sales Collateral Folder URL</Label>
            <Input
              id="sales_collateral_url"
              type="url"
              value={data.sales_collateral_url || ''}
              onChange={(e) => handleInputChange('sales_collateral_url', e.target.value)}
              placeholder="https://drive.google.com/drive/folders/..."
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Link to pitch decks, one-pagers, whitepapers, and other sales materials
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Other Assets */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-primary" />
            <CardTitle>Other Assets</CardTitle>
          </div>
          <CardDescription>Additional resources, documents, or notes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="other_assets_url">Other Assets URL</Label>
              <Input
                id="other_assets_url"
                type="url"
                value={data.other_assets_url || ''}
                onChange={(e) => handleInputChange('other_assets_url', e.target.value)}
                placeholder="https://..."
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="assets_notes">Additional Notes</Label>
              <Textarea
                id="assets_notes"
                value={data.assets_notes || ''}
                onChange={(e) => handleInputChange('assets_notes', e.target.value)}
                placeholder="Any additional information about assets, access requirements, or notes..."
                rows={4}
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Notes about asset locations, access permissions, or special instructions
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}



